import { StoreListingVariantPrices } from './StoreListingVariantPrices';
import type PriceStore from '../../../../UIData/_stores/PriceStore';
import type VariantPriceStore from '../../../../UserData/_stores/Store/VariantPriceStore';
import type { StoreVariantOptionTypes } from './StoreVariantLimit';

export interface StorePriceGroup {
	name: string
	listVariantPrices: VariantPriceStore[]
}

interface StorePriceRow {
	key: string
	group: StorePriceGroup
	name: string
	costPrice: PriceStore
	price: PriceStore
	mixedCosts: boolean
}

interface StorePriceSection {
	key: string
	name?: string
	rows: StorePriceRow[]
}

/** Combine equivalent sellable options across design variants, preserving every backing price ID. */
export default function buildStorePriceSections(
	groups: Map<string, StorePriceGroup>,
	availableOptionTypes: StoreVariantOptionTypes | undefined,
	locale: string,
): StorePriceSection[] {
	const collator = new Intl.Collator(locale.replace('_', '-'), { numeric: true, sensitivity: 'base' });
	const materialType = availableOptionTypes?.find(type => ['fabric', 'paper'].includes(type.get('slug')));
	// A material heading is useful only when rows have another pricing dimension.
	// Free choices such as crop-top sizes already share their fabric's price row.
	const groupByMaterial = materialType !== undefined && Array.from(groups.values()).some(group => {
		const variantPrice = group.listVariantPrices[0];
		const priceChoices = new Set(variantPrice.get('slug').split('|').map(choice => choice.split('$')[0]));
		return variantPrice.get('options').some(option => option.get('id_product_option_type') !== materialType.get('id')
			&& priceChoices.has(option.get('slug')));
	});
	const optionTypes = availableOptionTypes?.valueSeq()
		.filter(type => !groupByMaterial || type !== materialType)
		.sort((a, b) => a.get('theorder') - b.get('theorder') || a.get('id') - b.get('id'))
		.toArray() ?? [];
	const sections = new Map<string, StorePriceSection>();

	groups.forEach(group => {
		const options = group.listVariantPrices[0].get('options');
		const material = groupByMaterial ? options.find(option => option.get('id_product_option_type') === materialType.get('id')) : undefined;
		const sectionKey = material?.get('slug') ?? '';
		let section = sections.get(sectionKey);
		if (!section) {
			section = { key: sectionKey, name: material?.get('name'), rows: [] };
			sections.set(sectionKey, section);
		}

		// Variant IDs and dependency IDs describe which designs support a choice,
		// rather than a separate price for that finished size/fabric/color combination.
		const key = StoreListingVariantPrices.getChoiceKey(group.listVariantPrices[0]);
		const existing = section.rows.find(row => row.key === key);
		if (existing) {
			existing.group.listVariantPrices.push(...group.listVariantPrices);
			return;
		}
		// The material is already named by the section heading.
		const name = material
			? group.name.replace(`${material.get('name')} / `, '').replace(` / ${material.get('name')}`, '')
			: group.name;
		section.rows.push({
			key,
			group: { name: group.name, listVariantPrices: [...group.listVariantPrices] },
			name,
			costPrice: group.listVariantPrices[0].get('costPrice'),
			price: group.listVariantPrices[0].get('price'),
			mixedCosts: false,
		});
	});

	sections.forEach(section => {
		section.rows.forEach(row => {
			const first = row.group.listVariantPrices[0];
			row.group.listVariantPrices.forEach(variantPrice => {
				if (variantPrice.get('price').get('amount') > row.price.get('amount')) row.price = variantPrice.get('price');
				row.mixedCosts ||= !variantPrice.get('costPrice').equals(first.get('costPrice'));
				if (variantPrice.get('costPrice').get('amount') > row.costPrice.get('amount')) {
					row.costPrice = variantPrice.get('costPrice');
				}
			});
		});
		section.rows.sort((a, b) => {
			for (const type of optionTypes) {
				const optionA = a.group.listVariantPrices[0].get('options').find(option => option.get('id_product_option_type') === type.get('id'));
				const optionB = b.group.listVariantPrices[0].get('options').find(option => option.get('id_product_option_type') === type.get('id'));
				if (optionA && optionB) {
					const order = optionA.get('theorder') - optionB.get('theorder') || collator.compare(optionA.get('name'), optionB.get('name'));
					if (order !== 0) return order;
				}
			}
			return collator.compare(a.name, b.name)
				|| collator.compare(a.key, b.key);
		});
	});

	return Array.from(sections.values()).sort((a, b) => collator.compare(a.name ?? '', b.name ?? ''));
}
